import Eye from "../../../../assets/icons/eye.svg";
import { useTranslation } from "react-i18next";
import Download from "../../../../assets/icons/download.svg";
import { downloadDoc, openDoc } from '../../../../services/Api/documentService';
import { usePartnerRequestDetailsStore } from '../../../../stores/Cac/Partner/partnerRequestDetails';
import { dateFormat, fileNameFormat } from "../../../../services/Utils/functionService";
import { useNavigate } from "react-router-dom";
import Return from "../../../../assets/icons/return.svg";
import { useState } from "react";
import useSorting from "../../../../hooks/useSorting";

const PartnerDocumentsTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { documents } = usePartnerRequestDetailsStore();
  // Hooks
  const [sortedData, handleSort] = useSorting(documents);
  // State pour le tri
  const [sortField, setSortField] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Fonction de tri personnalisée
  const handleCustomSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    handleSort(field);
  };

    // Fonction pour obtenir l'icône de tri
    const getSortIcon = (field: string) => {
      if (sortField !== field) {
        return (
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
          </svg>
        );
      }
      
      if (sortDirection === "asc") {
        return (
          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        );
      } else {
        return (
          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        );
      }
    };

  return (
    <div className="w-full h-full overflow-y-scroll rounded-2xl border border-gray-100 bg-white">
      {/* Header de la table avec design moderne */}
      <div className="w-full bg-gray-50 p-4 border-b border-gray-200 rounded-t-2xl">                  
        {/* Bouton retour et barre de recherche */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              type="button"
              className="p-3 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl transition-all duration-200 hover:scale-105 flex items-center gap-2"
              onClick={() => navigate(-1)}
              title={t("common.back")}
              >
              <img src={Return} alt="return" className="w-5 h-5" />
            </button>
                  
          </div>
          {/* Filtres et tri */}
          <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 font-medium">{t("common.sortBy")}</span>
              
              {/* Bouton de tri par ID */}
              <button 
                onClick={() => handleCustomSort("fakeId")}
                className={`px-4 py-2 text-sm rounded-lg transition-all duration-200 flex items-center gap-2 cursor-pointer ${
                  sortField === "fakeId" 
                    ? "bg-blue-100 text-blue-700 border border-blue-200" 
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                }`}
              >
                <span>{t("common.id")}</span>
                {getSortIcon("fakeId")}
              </button>
              
              {/* Bouton de tri par catégorie */}
              <button 
                onClick={() => handleCustomSort("documentName")}
                className={`px-4 py-2 text-sm rounded-lg transition-all duration-200 flex items-center gap-2 cursor-pointer ${
                  sortField === "documentName" 
                    ? "bg-blue-100 text-blue-700 border border-blue-200" 
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                }`}
              >
                <span>{t("partner.document")}</span>
                {getSortIcon("documentName")}
              </button>
              
              {/* Bouton de tri par date limite */}
              <button 
                onClick={() => handleCustomSort("createdAt")}
                className={`px-4 py-2 text-sm rounded-lg transition-all cursor-pointer duration-200 flex items-center gap-2 ${
                  sortField === "createdAt" 
                    ? "bg-blue-100 text-blue-700 border border-blue-200" 
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                }`}
              >
                <span>{t("customerRequests.date")}</span>
                {getSortIcon("createdAt")}
              </button>
              
              {/* Bouton de tri par statut */}
              <button 
                onClick={() => handleCustomSort("firstName")}
                className={`px-4 py-2 text-sm rounded-lg transition-all duration-200 flex items-center gap-2 cursor-pointer ${
                  sortField === "firstName" 
                    ? "bg-blue-100 text-blue-700 border border-blue-200" 
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                }`}
              >
                <span>{t("common.creator")}</span>
                {getSortIcon("firstName")}
              </button>
            </div>
        </div>
      </div>

      {/* En-têtes des colonnes */}
      <div className="w-full grid md:grid-cols-6 gap-4 p-2 bg-gray-50 border-b border-gray-200 font-BarlowCondensed text-grey-trio">
        <div className="font-semibold text-gray-700 flex items-center gap-2">
          <span className="text-sm text-gray-600 font-BarlowCondensed font-bold">{t("partner.requestId")}</span>
        </div>
        <div className="col-span-2 font-semibold text-gray-700 flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          {t("partner.documentName")}
        </div>
        <div className="font-semibold text-gray-700 flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          {t("partner.depositedOn")}
        </div>
        <div className="font-semibold text-gray-700 flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          {t("partner.by")}
        </div>
        <div className="font-semibold text-gray-700 flex items-center justify-center gap-2">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {t("common.actions")}
        </div>
      </div>

      {/* Corps de la table */}
      <div className="w-full text-md font-BarlowCondensed">
        {sortedData?.map((document) => (
          <div
            key={document?.id}
            className="w-full hover:bg-blue-50 transition-all duration-300 border-b border-gray-100 last:border-b-0"
          >
            <div className="grid md:grid-cols-6 gap-4 p-4 items-center">
              {/* Id requête */}
              <div className="col-span-1">
                <span className="text-gray-700 font-medium text-lg">
                  {document?.fakeId}
                </span>
              </div>
              {/* Nom du document */}
              <div className="col-span-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <span className="text-gray-700 font-medium text-lg">
                    {fileNameFormat(document?.documentName)}
                  </span>
                </div>
              </div>

              {/* Date de dépôt */}
              <div>{dateFormat(document?.createdAt)}</div>

              {/* Créateur */}
              <div>{document?.firstName} {document?.lastName}</div>

              {/* Actions */}
              <div className="flex justify-center items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    openDoc(document.docRequestId, document.id);
                  }}
                  className="flex items-center justify-center p-2 bg-gray-100 hover:bg-blue-100 rounded-lg transition-colors duration-200 cursor-pointer"
                  title="Visualiser le document"
                >
                  <img
                    src={Eye}
                    alt="visualisation"
                    className="w-4 h-4"
                  />
                </button>
                
                <button
                  type="button"
                  onClick={() => {
                    downloadDoc(
                      document.docRequestId,
                      document.id,
                      document.documentName
                    );
                  }}
                  className="flex items-center justify-center p-2 bg-gray-100 hover:bg-blue-100 rounded-lg transition-colors duration-200 cursor-pointer"
                  title="Télécharger le document"
                >
                  <img
                    src={Download}
                    alt="téléchargement"
                    className="w-4 h-4"
                  />
                </button>
              </div>
            </div>
          </div>
        ))}
        
        {/* Message si aucun document */}
        {(!documents || documents.length === 0) && (
          <div className="w-full p-8 text-center">
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-200">
              <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-500 font-medium">Aucun document disponible</p>
              <p className="text-gray-400 text-sm">Les documents apparaîtront ici une fois déposés</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerDocumentsTable;
